// Estado da plataforma independente de React, sessão e transporte HTTP.
export type PlatformAvailabilityState = 'available' | 'paused' | 'unavailable' | 'recovering';

export interface PlatformAvailabilitySnapshot {
  state: PlatformAvailabilityState;
  ready: boolean;
  legacy: boolean;
  retryAfterSeconds: number;
  pendingCommands: number;
}

export interface PlatformProbeResult {
  state: 'available' | 'paused' | 'unavailable';
  ready: boolean;
  legacy?: boolean;
  retryAfterSeconds?: number;
}

export interface PlatformAbortSignal {
  readonly aborted: boolean;
  addEventListener?: AbortSignal['addEventListener'];
  removeEventListener?: AbortSignal['removeEventListener'];
}

type AvailabilityCode = 'platform_maintenance' | 'platform_unavailable' | 'platform_result_unknown';

/** Falhas consecutivas da sonda antes de abrir o aviso de reconexão. */
export const PROBE_FAILURES_BEFORE_NOTICE = 2;

export class PlatformAvailabilityError extends Error {
  readonly code: AvailabilityCode;
  readonly status = 503;
  readonly requestProcessed: false | undefined;
  readonly request_processed: false | undefined;

  constructor(code: AvailabilityCode, requestProcessed?: false) {
    super(code === 'platform_result_unknown'
      ? 'Não foi possível confirmar o resultado. Confira o registro antes de repetir a ação.'
      : 'O sistema está reconectando. Seus dados continuam nesta tela. Aguarde para tentar novamente.');
    this.name = 'PlatformAvailabilityError';
    this.code = code;
    this.requestProcessed = requestProcessed;
    this.request_processed = requestProcessed;
  }
}

export function isPlatformAvailabilityError(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return code === 'platform_maintenance' || code === 'platform_unavailable' || code === 'platform_result_unknown';
}

const initialSnapshot: PlatformAvailabilitySnapshot = {
  state: 'available', ready: true, legacy: false, retryAfterSeconds: 3, pendingCommands: 0,
};
let snapshot = initialSnapshot;
const listeners = new Set<() => void>();
let probe: (() => Promise<PlatformProbeResult>) | undefined;
let identity: () => string = () => '';
let pendingProbe: Promise<PlatformAvailabilitySnapshot> | null = null;
let monitoringReferences = 0;
let monitorTimer: ReturnType<typeof setTimeout> | undefined;
let evidenceRevision = 0;
let consecutiveProbeFailures = 0;
/** Investigação de borda: acelera a sonda sem abrir o aviso ainda. */
let softInvestigation = false;

export function configurePlatformAvailability(configuration: {
  probe: () => Promise<PlatformProbeResult>;
  identity: () => string;
}): void {
  probe = configuration.probe;
  identity = configuration.identity;
}

export const getPlatformAvailabilitySnapshot = (): PlatformAvailabilitySnapshot => snapshot;
export const getPlatformAvailabilityIdentity = (): string => identity();

export function subscribePlatformAvailability(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function publish(update: Partial<PlatformAvailabilitySnapshot>): void {
  const next = { ...snapshot, ...update };
  if (Object.keys(next).every(key => next[key as keyof typeof next] === snapshot[key as keyof typeof snapshot])) return;
  snapshot = next;
  listeners.forEach(listener => listener());
}

function rescheduleMonitorSoon(): void {
  if (monitorTimer) {
    clearTimeout(monitorTimer);
    monitorTimer = undefined;
  }
  scheduleMonitor();
}

export function reportPlatformInterruption(planned = false): void {
  if (planned) {
    // Pausa planejada: o aviso deve aparecer imediatamente.
    evidenceRevision += 1;
    softInvestigation = false;
    consecutiveProbeFailures = PROBE_FAILURES_BEFORE_NOTICE;
    publish({ state: 'paused', ready: false, legacy: false });
    rescheduleMonitorSoon();
    return;
  }

  // Falha de borda (rede/timeout/502): investiga sem derrubar a UI até a sonda confirmar.
  // Não incrementa evidenceRevision para não descartar uma sonda saudável já em voo.
  softInvestigation = true;
  rescheduleMonitorSoon();
  void checkPlatformAvailability();
}

export function reportPlatformPendingCommand(): void {
  publish({ pendingCommands: snapshot.pendingCommands + 1 });
}

export function acknowledgePlatformPendingCommands(): void {
  publish({ pendingCommands: 0 });
}

function publishUnavailable(result?: Pick<PlatformProbeResult, 'legacy' | 'retryAfterSeconds'>): void {
  softInvestigation = true;
  publish({
    state: 'unavailable',
    ready: false,
    legacy: result?.legacy === true,
    retryAfterSeconds: Math.min(30, Math.max(1, result?.retryAfterSeconds || 3)),
  });
}

export async function checkPlatformAvailability(): Promise<PlatformAvailabilitySnapshot> {
  if (pendingProbe) return pendingProbe;
  if (!probe) return snapshot;
  const revision = evidenceRevision;
  pendingProbe = (async () => {
    try {
      const result = await Promise.resolve().then(() => probe!());
      if (revision !== evidenceRevision) return snapshot;
      const ready = result.state === 'available' && result.ready;
      if (ready) {
        consecutiveProbeFailures = 0;
        softInvestigation = false;
        publish({
          state: 'available',
          ready: true,
          legacy: result.legacy === true,
          retryAfterSeconds: Math.min(30, Math.max(1, result.retryAfterSeconds || 3)),
        });
      } else if (result.state === 'paused') {
        consecutiveProbeFailures = PROBE_FAILURES_BEFORE_NOTICE;
        softInvestigation = false;
        publish({
          state: 'paused',
          ready: false,
          legacy: result.legacy === true,
          retryAfterSeconds: Math.min(30, Math.max(1, result.retryAfterSeconds || 3)),
        });
      } else {
        consecutiveProbeFailures += 1;
        if (consecutiveProbeFailures >= PROBE_FAILURES_BEFORE_NOTICE) publishUnavailable(result);
      }
    } catch {
      if (revision === evidenceRevision) {
        consecutiveProbeFailures += 1;
        if (consecutiveProbeFailures >= PROBE_FAILURES_BEFORE_NOTICE) publishUnavailable();
      }
    } finally {
      pendingProbe = null;
    }
    return snapshot;
  })();
  return pendingProbe;
}

function scheduleMonitor(): void {
  if (!monitoringReferences || monitorTimer) return;
  const delay = (snapshot.ready && !softInvestigation ? 12_000 : 2_000) + Math.floor(Math.random() * 300);
  monitorTimer = setTimeout(async () => {
    monitorTimer = undefined;
    await checkPlatformAvailability();
    scheduleMonitor();
  }, delay);
}

export function startPlatformAvailabilityMonitoring(): () => void {
  monitoringReferences += 1;
  if (monitoringReferences === 1) void checkPlatformAvailability().then(scheduleMonitor);
  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    monitoringReferences = Math.max(0, monitoringReferences - 1);
    if (!monitoringReferences && monitorTimer) {
      clearTimeout(monitorTimer);
      monitorTimer = undefined;
    }
  };
}

export function waitForPlatformAvailability(options: {
  signal?: PlatformAbortSignal;
  timeoutMs?: number;
  identity?: string;
} = {}): Promise<void> {
  const expectedIdentity = options.identity ?? identity();
  if (options.signal?.aborted || expectedIdentity !== identity()) {
    return Promise.reject(new PlatformAvailabilityError('platform_unavailable', false));
  }
  if (snapshot.ready) return Promise.resolve();
  return new Promise((resolve, reject) => {
    let finished = false;
    let unsubscribe = () => {};
    const stopMonitor = startPlatformAvailabilityMonitoring();
    const finish = (error?: Error) => {
      if (finished) return;
      finished = true;
      clearTimeout(timeout);
      unsubscribe();
      stopMonitor();
      options.signal?.removeEventListener?.('abort', abort);
      if (error) reject(error); else resolve();
    };
    const abort = () => finish(new PlatformAvailabilityError('platform_unavailable', false));
    const timeout = setTimeout(abort, options.timeoutMs ?? 45_000);
    unsubscribe = subscribePlatformAvailability(() => {
      if (expectedIdentity !== identity()) return abort();
      if (snapshot.ready) finish();
    });
    options.signal?.addEventListener?.('abort', abort, { once: true });
    if (snapshot.ready) finish();
  });
}

export interface PlatformRequest {
  method?: string;
  url?: string;
  baseURL?: string;
  params?: Record<string, unknown> | URLSearchParams;
}

function getPlatformRequestUrl(request: PlatformRequest): URL | null {
  let url: URL;
  try { url = new URL(request.url || '', request.baseURL || 'https://app.sminy.ai'); } catch { return null; }
  const params = new URLSearchParams(url.search);
  if (request.params instanceof URLSearchParams) {
    request.params.forEach((value, key) => params.append(key, value));
  } else {
    Object.entries(request.params || {}).forEach(([key, value]) => params.append(key, String(value)));
  }
  url.search = params.toString();
  return url;
}

export function isPlatformCommand(request: PlatformRequest): boolean {
  if (!['get', 'head', 'options'].includes(String(request.method || 'get').toLowerCase())) return true;
  const url = getPlatformRequestUrl(request);
  return Boolean(url && ['sync', 'refresh', 'force', 'force_refresh', 'reprocess'].some(key =>
    url.searchParams.getAll(key).some(value => !['false', '0', ''].includes(value.toLowerCase()))));
}

export function isSafePlatformQuery(request: PlatformRequest): boolean {
  if (String(request.method || 'get').toLowerCase() !== 'get') return false;
  const url = getPlatformRequestUrl(request);
  if (!url) return false;
  // Alguns GETs sincronizam provedores. Toda opção de efeito exclui retomada.
  if (isPlatformCommand(request)) return false;
  const path = url.pathname.replace(/\/$/, '');
  return [
    /^\/auth\/browser\/session$/,
    /^\/api\/(?:clinic(?:\/extended)?|client-clinics|billing\/(?:summary|subscription-options))$/,
    /^\/api\/agenda\/clients\/\d+\/clinics\/\d+\/(?:crm\/(?:initial|leads|appointments|noShows|attendances|sales|lost)|(?:leads|agendamentos|comparecimentos|vendas|noshow-events)(?:\/\d+)?)$/,
    /^\/api\/reports\/(?:monthly|summary|usage-overview)$/,
    /^\/(?:api\/)?metrics\/(?:funnels(?:\/timeseries)?|funnel_by_source|timeline|projections|time_between_stages|ai_vs_humano|time_distribution|weekly_heatmap)$/,
    /^\/agent-config\/\d+$/,
    /^\/api\/(?:users|teams)(?:\/\d+)?$/,
    /^\/api\/(?:tags\/(?:management(?:\/\d+)?|categories)|notifications\/pending|tasks\/(?:upcoming|statistics))$/,
    /^\/api\/members\/(?:me|dashboard)$/,
    /^\/api\/chat-optimized\/messages\/paged$/,
    /^\/webhook\/(?:contacts|history|contacts\/no-history)$/,
  ].some(pattern => pattern.test(path));
}
